import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState
} from 'react'
import ResizeObserver from 'rc-resize-observer'
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List as VirtualizedList,
  WindowScroller
} from 'react-virtualized'
import List from '@material-ui/core/List'
import LOG from '../../logging/Logger'

const cacheSettings = { fixedWidth: true, defaultHeight: 58 }

const useWindowSize = () => {
  const [size, setSize] = useState([0, 0])

  useLayoutEffect(() => {
    const updateSize = () => {
      setSize([window.innerWidth, window.innerHeight])
    }
    window.addEventListener('resize', updateSize)
    updateSize()
    return () => window.removeEventListener('resize', updateSize)
  }, [])
  return size
}

const renderRow = ({ index, key, style, cache, parent, renderListItem }) => {
  const child = renderListItem({ index })
  if (!child) {
    return null
  }

  return (
    <CellMeasurer
      key={key}
      cache={cache}
      parent={parent}
      columnIndex={0}
      rowIndex={index}
    >
      {({ measure }) => (
        <div key={key} style={style}>
          <ResizeObserver onResize={measure}>{child}</ResizeObserver>
        </div>
      )}
    </CellMeasurer>
  )
}

export default memo(
  ({
    scrollRef,
    rowCount,
    renderListItem,
    estimatedRowSize = 100,
    droppableProvided,
    virtualThreshold = 0
  }) => {
    LOG.debug('Render: VirtualList: ' + estimatedRowSize)

    const [cache, setCache] = useState(new CellMeasurerCache(cacheSettings))
    const [windowWidth, windowHeight] = useWindowSize()

    useLayoutEffect(() => {
      setCache(new CellMeasurerCache(cacheSettings))
    }, [windowWidth, windowHeight])

    useEffect(() => {
      return () => {
        cache.clearAll()
      }
    }, [cache])

    const rowRenderer = useCallback(
      ({ index, key, style, parent }) =>
        renderRow({
          index,
          key,
          style,
          cache,
          parent,
          renderListItem
        }),
      [cache, renderListItem]
    )

    const getAllRows = useCallback(() => {
      const rows = []
      for (let i = 0; i < rowCount; i++) {
        const child = renderListItem({ index: i })
        if (child) {
          rows.push(child)
        }
      }
      return rows
    }, [renderListItem, rowCount])

    return (
      <Fragment>
        {rowCount > virtualThreshold && (
          <WindowScroller scrollElement={scrollRef || window}>
            {({ height, registerChild, isScrolling, scrollTop }) => (
              <AutoSizer disableHeight>
                {({ width }) => (
                  <div ref={el => registerChild(el)}>
                    <VirtualizedList
                      autoHeight
                      style={{ outline: 'none' }}
                      isScrolling={isScrolling}
                      scrollTop={scrollTop}
                      width={width}
                      height={height ? height : window.innerHeight}
                      rowHeight={cache.rowHeight}
                      rowRenderer={rowRenderer}
                      rowCount={rowCount}
                      overscanRowCount={10}
                      deferredMeasurementCache={cache}
                      estimatedRowSize={estimatedRowSize}
                    />
                  </div>
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
        {rowCount <= virtualThreshold && (
          <List style={{ paddingTop: 0 }}>
            {getAllRows().map(row => row)}
            {droppableProvided.placeholder}
          </List>
        )}
      </Fragment>
    )
  }
)
